import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ParameterTable = ({ parameters, title = "Parameters" }) => {
  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (paramName) => {
    setExpandedRows(prev => ({
      ...prev,
      [paramName]: !prev[paramName]
    }));
  };

  const getTypeColor = (type) => {
    const colors = {
      string: 'text-blue-600 bg-blue-50',
      number: 'text-green-600 bg-green-50',
      boolean: 'text-purple-600 bg-purple-50',
      array: 'text-orange-600 bg-orange-50',
      object: 'text-red-600 bg-red-50'
    };
    return colors[type] || 'text-gray-600 bg-gray-50';
  };

  if (!parameters || parameters.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
      
      <div className="overflow-x-auto">
        <table className="w-full border border-border rounded-lg">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-b border-border">
                Parameter
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-b border-border">
                Type
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-b border-border">
                Required
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-b border-border">
                Description
              </th>
              <th className="px-4 py-3 text-center text-sm font-medium text-text-primary border-b border-border w-12">
                Details
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface">
            {parameters.map((param, index) => (
              <React.Fragment key={param.name}>
                <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors duration-200`}>
                  <td className="px-4 py-3 border-b border-border">
                    <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                      {param.name}
                    </code>
                  </td>
                  <td className="px-4 py-3 border-b border-border">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(param.type)}`}>
                      {param.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 border-b border-border">
                    {param.required ? (
                      <span className="inline-flex items-center space-x-1">
                        <Icon name="Check" size={14} className="text-success" />
                        <span className="text-sm text-success font-medium">Yes</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center space-x-1">
                        <Icon name="X" size={14} className="text-text-secondary" />
                        <span className="text-sm text-text-secondary">No</span>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 border-b border-border">
                    <p className="text-sm text-text-primary">{param.description}</p>
                  </td>
                  <td className="px-4 py-3 border-b border-border text-center">
                    {(param.example || param.enum || param.format) && (
                      <button
                        onClick={() => toggleRow(param.name)}
                        className="p-1 hover:bg-gray-200 rounded transition-colors duration-200"
                      >
                        <Icon 
                          name="ChevronDown" 
                          size={16} 
                          className={`text-text-secondary transition-transform duration-200 ${
                            expandedRows[param.name] ? 'rotate-180' : ''
                          }`} 
                        />
                      </button>
                    )}
                  </td>
                </tr>
                
                {expandedRows[param.name] && (
                  <tr className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                    <td colSpan="5" className="px-4 py-3 border-b border-border">
                      <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                        {param.example && (
                          <div>
                            <h5 className="text-sm font-medium text-text-primary mb-2">Example:</h5>
                            <code className="text-sm bg-white p-2 rounded border block">
                              {typeof param.example === 'string' ? param.example : JSON.stringify(param.example)}
                            </code>
                          </div>
                        )}
                        
                        {param.enum && (
                          <div>
                            <h5 className="text-sm font-medium text-text-primary mb-2">Allowed Values:</h5>
                            <div className="flex flex-wrap gap-2">
                              {param.enum.map((value, idx) => (
                                <span key={idx} className="px-2 py-1 bg-white border rounded text-sm">
                                  {value}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {param.format && (
                          <div>
                            <h5 className="text-sm font-medium text-text-primary mb-2">Format:</h5>
                            <span className="text-sm text-text-secondary">{param.format}</span>
                          </div>
                        )}
                        
                        {param.default !== undefined && (
                          <div>
                            <h5 className="text-sm font-medium text-text-primary mb-2">Default Value:</h5>
                            <code className="text-sm bg-white p-2 rounded border">
                              {typeof param.default === 'string' ? param.default : JSON.stringify(param.default)}
                            </code>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ParameterTable;